import { useEffect, useId, useRef, useState } from 'react';

/**
 * Sentinel value standing in for the "(Not set)" option inside a MultiSelect's
 * `selected` array. Re-exported from companyFilter as FIELD_NOT_SET — kept in
 * sync there; this local copy keeps the component dependency-free of the filter
 * module. Callers that want a "(Not set)" choice pass `includeNotSet` and treat
 * this token in `selected`/`onChange` like any other value.
 */
export const MULTISELECT_NOT_SET = '__none__';

// Matches the closed-control styling of Field's <Select> (border/padding/text),
// but it's a real <button> so it can host the count badge and toggle a panel.
const triggerCls =
  'flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:border-slate-300 focus:border-slate-400 focus:outline-none';

/**
 * Accessible multi-select dropdown. A trigger button shows the field label and,
 * when anything is selected, a count badge; clicking it opens a panel of
 * checkboxes (one per allowed option, plus an optional "(Not set)" row). Closes
 * on outside-click or Escape. Self-contained and dependency-free.
 *
 * `selected` is the controlled set of chosen values (a subset of `values`, plus
 * MULTISELECT_NOT_SET when `includeNotSet` and the not-set row is ticked).
 * Selecting nothing means "no filter" — the caller decides what that implies.
 */
export function MultiSelect({
  label,
  values,
  selected,
  onChange,
  includeNotSet = false,
  labels,
  className = '',
}: {
  label: string;
  /** Allowed options, in display order. */
  values: string[];
  /** Currently-checked values (subset of `values` + optional MULTISELECT_NOT_SET). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** When true, render a "(Not set)" checkbox backed by MULTISELECT_NOT_SET. */
  includeNotSet?: boolean;
  /** Optional display text per value (e.g. company id → name). Falls back to the
   *  value itself when a value is absent, so callers whose values are already
   *  human-readable can omit it entirely. */
  labels?: Record<string, string>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  // Close on outside pointerdown or Escape while the panel is open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectedSet = new Set(selected);
  const toggle = (value: string) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // Preserve a stable order: configured options first (in `values` order),
    // then the not-set sentinel last, so the URL/param order is deterministic.
    const ordered = values.filter((v) => next.has(v));
    if (next.has(MULTISELECT_NOT_SET)) ordered.push(MULTISELECT_NOT_SET);
    onChange(ordered);
  };

  const count = selected.length;

  const options: { value: string; text: string }[] = values.map((v) => ({
    value: v,
    text: labels?.[v] ?? v,
  }));
  if (includeNotSet) options.push({ value: MULTISELECT_NOT_SET, text: '(Not set)' });

  return (
    <div ref={rootRef} className={`relative sm:w-52 ${className}`}>
      <button
        type="button"
        className={triggerCls}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Filter by ${label}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label}</span>
          {count > 0 && (
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-900 px-1.5 text-xs font-medium text-white">
              {count}
            </span>
          )}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-labelledby={labelId}
          className="absolute left-0 z-20 mt-1 max-h-64 w-full min-w-max overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          <span id={labelId} className="sr-only">
            {label}
          </span>
          {options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-400">No options</p>
          ) : (
            options.map((opt) => {
              const checked = selectedSet.has(opt.value);
              return (
                <label
                  key={opt.value}
                  role="option"
                  aria-selected={checked}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                  />
                  <span className="truncate">{opt.text}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
